import {html, LitElement, svg, type TemplateResult, unsafeCSS} from "lit";
import {Task} from "@lit/task";
import {customElement, property, state} from "lit/decorators.js";
import styles from "./IdsIconElement.css?inline";
import {ROOT_TAG} from "../../globals";
import {unsafeStatic} from "lit/static-html.js";

const tagName = `${ROOT_TAG}-interstate-icon`;
export const InterstateIcon = unsafeStatic(tagName);

@customElement(tagName)
export class IdsIconElement extends LitElement {
  @property()
  name?: string;

  @property()
  version?: string = "1.0.4";

  @property()
  svg?: string;

  @property()
  url?: string;

  @state()
  protected _url = "";

  @state()
  protected _frag: TemplateResult<2> | null = null;

  private _toSVG(htmlString: string) {
    const div = document.createElement("div");
    div.innerHTML = htmlString;
    return div.firstChild;
  }

  private _renderSvg(glyphString: string) {
    if (glyphString) {
      let svgString = glyphString;

      if (svgString.indexOf("data:") === 0) {
        svgString = atob(glyphString.substring(glyphString.indexOf(",") + 1));
      }

      if (svgString.indexOf("<svg") < 0) {
        return;
      }
      const svgStart = svgString.indexOf("<svg");
      const svgEnd = svgString.indexOf("</svg>") + "</svg>".length;

      svgString = svgString.substring(svgStart, svgEnd);

      const svgEl = this._toSVG(svgString) as SVGElement;

      if (svgEl.childElementCount > 0) {
        const children = Array.from(svgEl.children);

        const frag = [
          children.map(f => f.outerHTML).join("")
        ] as unknown as TemplateStringsArray;
        (frag as {raw: TemplateStringsArray["raw"]}).raw = frag;

        this._frag = svg(frag);
      }
    }
  }

  private _fetchUrlTask = new Task(this, {
    task: async ([url], {signal}) => {
      if (!url) {
        return;
      }
      const data = await fetch(url, {
        method: "GET",
        mode: "cors",
        signal
      });
      if (data.ok) {
        this._renderSvg(await data.text());
      }
    },
    args: () => [this._url, this.version]
  });

  override connectedCallback() {
    super.connectedCallback();
    this._processAttrs();
  }

  override attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null
  ) {
    super.attributeChangedCallback(name, _old, value);
    this._processAttrs();
  }

  private _processAttrs() {
    const prevUrl = this._url;

    if (this.url) {
      this._url = this.url;
    } else if (this.name) {
      const version = this.version;
      const name = this.name
        .split(" ")
        .map(s => s.replace(/([A-Z])/g, "-$1").replace(/^-/, ""))
        .join("-")
        .toLowerCase()
        .replace(/--/g, "-")
        .replace(/-icon$/, "");
      this._url = `https://cdn.interstate.coxautoinc.com/${version}/icons/${name}.svg`;
    }

    if (this.svg) {
      this._renderSvg(this.svg);
    } else if (this._url !== prevUrl) {
      this._frag = null;
    }
  }

  static styles = unsafeCSS(styles);

  override render() {
    const emptySvg = html` <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      class="ids-icon"></svg>`;

    const fragSvg = html` <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      class="ids-icon">
      ${this._frag}
    </svg>`;

    if (this._url && !this._frag) {
      return this._fetchUrlTask.render({
        pending: () => emptySvg,
        complete: () => fragSvg,
        error: () => emptySvg
      });
    } else if (this._frag) {
      return fragSvg;
    }

    return emptySvg;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: IdsIconElement;
  }
}
